import {
  Plus,
  Building2,
  Search,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Card } from "@/components/ui/card";
import { SortToolbar } from "@/components/ui/sort-toolbar";
import { PropertyCard } from "./property-card";
import { PropertiesTable } from "./properties-table";
import { PropertiesSummaryBar } from "./properties-summary-bar";
import { PropertyModal } from "./property-modal";
import { useActiveRole } from "@/features/auth/hooks/use-active-role";
import { UserRole } from "@/types/api";
import { useViewMode } from "../hooks/use-view-mode";
import type { PropertiesPageState } from "../hooks/use-properties-page";
import { cn } from "@/lib/utils";

export function PropertiesView({ state }: { state: PropertiesPageState }) {
  const activeRole = useActiveRole();
  const canWrite = activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;
  // List is the default; choice persists per-user via localStorage
  // under a dedicated /properties key so it doesn't cross-pollinate
  // with the Property Portal device view mode.
  const [viewMode, setViewMode] = useViewMode('norgas.properties.viewMode', 'list');
  const {
    properties,
    propertiesTotalPages,
    propertiesTotalCount,
    propertiesPage,
    sortBy,
    sortDir,
    handleSortChange,
    handleSortDirChange,
    searchInput,
    setSearchInput,
    handlePropertiesPageChange,
    editingProperty,
    isPropertyModalOpen,
    setIsPropertyModalOpen,
    deletingProperty,
    isDeletingProperty,
    setDeletingProperty,
    handleAddProperty,
    handleDeletePropertyConfirm,
    handleSaveProperty,
    handleBulkCreateProperty,
  } = state;

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header with search, view toggle, and add */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">My Properties</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your properties and devices.</p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto flex-wrap">
              <div className="relative flex-1 sm:w-64 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search properties..."
                  className="pl-9"
                />
              </div>
              {/* Grid/List toggle. List is default; persists per user. */}
              <div
                role="group"
                aria-label="Property view mode"
                className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-0.5 flex-shrink-0"
              >
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  aria-pressed={viewMode === 'list'}
                  title="List view"
                  className={cn(
                    'inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors',
                    viewMode === 'list'
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100',
                  )}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  aria-pressed={viewMode === 'grid'}
                  title="Grid view"
                  className={cn(
                    'inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors',
                    viewMode === 'grid'
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100',
                  )}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
              {canWrite && (
                <Button onClick={handleAddProperty} className="flex-shrink-0 whitespace-nowrap">
                  <Plus className="w-5 h-5" />
                  Add Property
                </Button>
              )}
            </div>
          </div>

          {/* Summary Stats Bar */}
          <PropertiesSummaryBar
            totalProperties={propertiesTotalCount}
            metersConfigured={properties.reduce((sum, p) => sum + (p.deviceCount ?? 0), 0)}
            activeAlarms={properties.reduce((sum, p) => sum + (p.activeAlarmCount ?? 0), 0)}
          />

          {/* Sort toolbar — only meaningful when there are rows. Wraps
              below the page header on narrow viewports. */}
          {properties.length > 0 && viewMode === 'list' && (
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
              <SortToolbar
                sortBy={sortBy}
                sortDir={sortDir}
                onSortByChange={handleSortChange}
                onSortDirChange={handleSortDirChange}
                options={[
                  { value: 'name', label: 'Name' },
                  { value: 'buildingCount', label: 'Buildings' },
                  { value: 'unitCount', label: 'Units' },
                  { value: 'deviceCount', label: 'Meters' },
                  { value: 'activeAlarmCount', label: 'Active Alerts' },
                  { value: 'createdAt', label: 'Created' },
                ]}
              />
            </div>
          )}

          {properties.length === 0 ? (
            <Card variant="elevated">
              <EmptyState
                icon={<Building2 className="w-16 h-16" />}
                title={
                  propertiesTotalCount === 0
                    ? "No properties yet"
                    : "No properties match your search"
                }
                description={
                  propertiesTotalCount === 0
                    ? "Get started by adding your first property."
                    : "Try adjusting your search."
                }
                action={
                  propertiesTotalCount === 0 && canWrite
                    ? { label: "Add Property", onClick: handleAddProperty }
                    : undefined
                }
              />
            </Card>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {properties.map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
              ) : (
                <PropertiesTable
                  properties={properties}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSortChange={handleSortChange}
                />
              )}

              {propertiesTotalPages > 1 && (
                <div className="flex items-center justify-center sm:justify-between">
                  <p className="hidden sm:block text-sm text-gray-600">
                    {propertiesTotalCount} total properties
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePropertiesPageChange(propertiesPage - 1)}
                      disabled={propertiesPage <= 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Page {propertiesPage} of {propertiesTotalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePropertiesPageChange(propertiesPage + 1)}
                      disabled={propertiesPage >= propertiesTotalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isPropertyModalOpen && (
        <PropertyModal
          property={editingProperty}
          onClose={() => setIsPropertyModalOpen(false)}
          onSave={handleSaveProperty}
          onBulkCreate={handleBulkCreateProperty}
        />
      )}

      <ConfirmDialog
        isOpen={!!deletingProperty}
        title="Delete Property"
        description={`Are you sure you want to delete "${deletingProperty?.name}" and all its associated units? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeletePropertyConfirm}
        onCancel={() => setDeletingProperty(null)}
        isLoading={isDeletingProperty}
      />
    </>
  );
}
